import { useState, useCallback } from 'react';
import { Settings } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { cn } from '@/utils';
import { COOKIE__URL } from '@/constants';
import { usePointerActivation } from '@/hooks';
import { useCurrentAccessTokenSelector } from '@/store/slices/auth/session.slice';
import { useUpdateCookieSettingsMutation } from '@/store/api';
import {
	setCookieConsent,
	type CookieConsentPreferences,
	type CookieConsentState,
} from '@/store/slices/cookie-consent.slice';
import {
	CARD_CLS,
	CookiePreferencesDialog,
	saveConsent,
	type CookieCategoryState,
} from './cookie-preferences.shared';

const WRAPPER_CLS =
	'fixed left-1/2 -translate-x-1/2 lg:left-4 lg:translate-x-0 z-101 w-[calc(100%-32px)] bottom-[90px] lg:bottom-6';

interface BannerProps {
	onAcceptAll: () => void;
	onManagePreferences: () => void;
}

function CookieConsentBanner({ onAcceptAll, onManagePreferences }: BannerProps) {
	const manageHandlers = usePointerActivation(onManagePreferences);
	const acceptHandlers = usePointerActivation(onAcceptAll);

	return (
		<div className={cn(WRAPPER_CLS, 'max-w-[363px]')}>
			<div className={cn(CARD_CLS, 'p-4 gap-4')}>
				<p className="text-[14px] leading-[20px] tracking-[-0.028em] text-[#2a3e5b] font-normal">
					This website uses cookies to improve your experience. By clicking &ldquo;Accept&rdquo; you
					are agreeing to our{' '}
					<a
						href={COOKIE__URL}
						target="_blank"
						rel="noreferrer"
						className="text-[#5583f6] hover:underline"
					>
						Cookie Policy.
					</a>
				</p>

				<div className="flex items-center justify-between h-10">
					<button
						type="button"
						{...manageHandlers}
						className="flex items-center gap-2 pl-0.5 text-[#5583f6] hover:opacity-80 transition-opacity"
					>
						<Settings size={20} strokeWidth={1.5} />
						<span className="text-[14px] font-medium leading-[20px] tracking-[-0.364px] whitespace-nowrap">
							Manage Preferences
						</span>
					</button>

					<button
						type="button"
						{...acceptHandlers}
						className={cn(
							'h-10 w-[88px] rounded-[8px]',
							'bg-blue-custom-2 border border-[#5583f6]',
							'shadow-[0px_8px_16px_0px_rgba(38,103,255,0.24),0px_2px_6px_0px_rgba(0,0,0,0.15)]',
							'text-[14px] font-medium leading-[20px] tracking-[-0.364px] text-white',
							'hover:bg-[#1a4fd6] transition-colors'
						)}
					>
						Accept
					</button>
				</div>
			</div>
		</div>
	);
}

export function CookieConsent() {
	const dispatch = useDispatch();
	const consentValue = useSelector(
		(state: { cookieConsent: CookieConsentState }) => state.cookieConsent.value
	);
	const accessToken = useCurrentAccessTokenSelector();
	const [updateCookieSettings] = useUpdateCookieSettingsMutation();
	const [showPreferences, setShowPreferences] = useState(false);
	const [categoryState, setCategoryState] = useState<CookieCategoryState>({
		preference: false,
		performance: false,
		marketing: false,
	});

	const allSelected = Object.values(categoryState).every(Boolean);

	const handleSelectAll = (checked: boolean) => {
		setCategoryState({ preference: checked, performance: checked, marketing: checked });
	};

	const handleToggle = (key: keyof CookieCategoryState, checked: boolean) => {
		setCategoryState((prev: CookieCategoryState) => ({ ...prev, [key]: checked }));
	};

	const persist = useCallback(
		(preferences: CookieConsentPreferences) => {
			saveConsent(preferences);
			dispatch(setCookieConsent(preferences));
			// Logged-in users also sync their choice to the backend; guests stay on localStorage.
			if (accessToken) {
				updateCookieSettings(preferences);
			}
		},
		[dispatch, accessToken, updateCookieSettings]
	);

	const handleAcceptAll = useCallback(() => {
		persist({ essential: true, preference: true, performance: true, marketing: true });
	}, [persist]);

	const handleRejectAll = () => {
		persist({ essential: true, preference: false, performance: false, marketing: false });
	};

	const handleAcceptAndSave = () => {
		persist({ essential: true, ...categoryState });
	};

	const handleManagePreferences = useCallback(() => {
		setShowPreferences(true);
	}, []);

	if (consentValue !== null) return null;

	if (showPreferences) {
		return (
			<CookiePreferencesDialog
				categoryState={categoryState}
				allSelected={allSelected}
				onSelectAll={handleSelectAll}
				onToggle={handleToggle}
				onRejectAll={handleRejectAll}
				onAcceptAndSave={handleAcceptAndSave}
				onClose={() => setShowPreferences(false)}
			/>
		);
	}

	return (
		<CookieConsentBanner
			onAcceptAll={handleAcceptAll}
			onManagePreferences={handleManagePreferences}
		/>
	);
}
